import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Award,
  Trophy,
  Star,
  Zap,
  Crown,
  RefreshCw,
  Lock,
  Check,
  TrendingUp,
  Users,
  Medal,
  Share2,
  Linkedin,
  Twitter,
  X
} from "lucide-react";

// Icon mapping
const BADGE_ICONS = {
  "user": Award,
  "check-circle": Check,
  "star": Star,
  "send": Zap,
  "zap": Zap,
  "award": Award,
  "video": Trophy,
  "trophy": Trophy,
  "target": TrendingUp,
  "briefcase": Medal,
  "flame": Zap,
  "users": Users,
  "message-square": Award,
  "heart": Star,
  "rocket": Zap,
  "crown": Crown,
  "link": Award
};

const BadgesPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [badgeData, setBadgeData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [shareModal, setShareModal] = useState(null);

  const shareBadgeOnLinkedIn = (badge) => {
    const text = encodeURIComponent(`I just earned the "${badge.name}" badge on Rekrutor! ${badge.description}`);
    const url = encodeURIComponent("https://rekrutor.com/badges");
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "_blank");
    setShareModal(null);
    toast.success("Opening LinkedIn to share!");
  };

  const shareBadgeOnTwitter = (badge) => {
    const text = encodeURIComponent(`I just earned the "${badge.name}" badge on @Rekrutor! ${badge.description} #CareerGoals #Achievement`);
    const url = encodeURIComponent("https://rekrutor.com/badges");
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
    setShareModal(null);
    toast.success("Opening Twitter to share!");
  };

  const copyBadgeLink = async (badge) => {
    const shareText = `I just earned the "${badge.name}" badge on Rekrutor! ${badge.description} - Join me at https://rekrutor.com`;
    await navigator.clipboard.writeText(shareText);
    setShareModal(null);
    toast.success("Badge info copied to clipboard!");
  };

  useEffect(() => {
    fetchBadges();
    fetchLeaderboard();
  }, []);

  const fetchBadges = async () => {
    try {
      const response = await fetch(`${API}/gamification/my-badges`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setBadgeData(data);
      }
    } catch (error) {
      console.error("Failed to fetch badges:", error);
      toast.error("Failed to load badges");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API}/gamification/leaderboard`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    }
  };

  const checkAchievements = async () => {
    setChecking(true);
    try {
      const response = await fetch(`${API}/gamification/check-achievements`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.newly_earned.length > 0) {
          toast.success(`🏆 You earned ${data.newly_earned.length} new badge(s)!`);
          fetchBadges();
        } else {
          toast.info("No new badges yet. Keep going!");
        }
      }
    } catch (error) {
      toast.error("Failed to check achievements");
    } finally {
      setChecking(false);
    }
  };

  const categories = [
    { id: "all", label: "All Badges" },
    { id: "profile", label: "Profile" },
    { id: "applications", label: "Applications" },
    { id: "interviews", label: "Interviews" },
    { id: "recruiting", label: "Recruiting" },
    { id: "community", label: "Community" },
    { id: "special", label: "Special" }
  ];

  const filteredEarned = selectedCategory === "all" 
    ? badgeData?.earned || []
    : (badgeData?.earned || []).filter(b => b.category === selectedCategory);

  const filteredAvailable = selectedCategory === "all"
    ? badgeData?.available || []
    : (badgeData?.available || []).filter(b => b.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Sidebar />
        <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
          <div className="flex items-center justify-center h-64">
            <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center animate-pulse">
              <Trophy className="w-8 h-8 text-crimson" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
              ACHIEVEMENTS & <span className="text-crimson">BADGES</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">
              {badgeData?.total_earned || 0} of {badgeData?.total_available || 0} badges • {badgeData?.total_points || 0} pts
            </p>
          </div>
          <Button
            onClick={checkAchievements}
            disabled={checking}
            className="bg-crimson hover:bg-crimson-hover w-full sm:w-auto"
          >
            {checking ? (
              <RefreshCw className="w-4 h-4 sm:mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 sm:mr-2" />
            )}
            <span className="sm:inline">Check Badges</span>
          </Button>
        </div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-obsidian-light border border-[#222] p-4 sm:p-6 mb-6 sm:mb-8"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-sm sm:text-base">Badge Completion</span>
            <span className="text-crimson font-bold">{badgeData?.completion_percentage || 0}%</span>
          </div>
          <div className="h-2 sm:h-3 bg-[#222] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${badgeData?.completion_percentage || 0}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-crimson to-red-500"
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-4 sm:space-y-6">
            {/* Category Filter */}
            <div className="flex gap-1.5 sm:gap-2 flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm transition-colors ${
                    selectedCategory === cat.id
                      ? "bg-crimson text-white"
                      : "bg-obsidian-light border border-[#333] text-gray-400 hover:border-crimson"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Earned Badges */}
            {filteredEarned.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              >
                <h3 className="font-heading text-base sm:text-lg font-bold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-crimson" />
                  Earned Badges ({filteredEarned.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  <AnimatePresence>
                    {filteredEarned.map((badge, idx) => {
                      const IconComponent = BADGE_ICONS[badge.icon] || Award;
                      return (
                        <motion.div
                          key={badge.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: idx * 0.05 }}
                          className="p-3 sm:p-4 text-center relative group overflow-hidden"
                          style={{
                            background: badge.rarity_colors?.bg,
                            border: `1px solid ${badge.rarity_colors?.border}`
                          }}
                        >
                          {/* Share Button */}
                          <button
                            onClick={() => setShareModal(badge)}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-black/50 hover:bg-crimson"
                            data-testid={`share-badge-${badge.id}`}
                          >
                            <Share2 className="w-3.5 h-3.5 text-white" />
                          </button>
                          <div 
                            className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 flex items-center justify-center flex-shrink-0"
                            style={{ background: `${badge.rarity_colors?.border}40` }}
                          >
                            <IconComponent 
                              className="w-5 h-5 sm:w-6 sm:h-6" 
                              style={{ color: badge.rarity_colors?.text }}
                            />
                          </div>
                          <h4 
                            className="font-bold text-xs sm:text-sm mb-1 line-clamp-2"
                            style={{ color: badge.rarity_colors?.text }}
                          >
                            {badge.name}
                          </h4>
                          <p className="text-xs text-gray-500 mb-2 line-clamp-2 hidden sm:block">{badge.description}</p>
                          <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                            <span 
                              className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 uppercase"
                              style={{ 
                                background: badge.rarity_colors?.border,
                                color: badge.rarity_colors?.text
                              }}
                            >
                              {badge.rarity}
                            </span>
                            <span className="text-[10px] sm:text-xs text-crimson">+{badge.points} pts</span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}

            {/* Locked Badges */}
            {filteredAvailable.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              >
                <h3 className="font-heading text-base sm:text-lg font-bold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-gray-500" />
                  Locked Badges ({filteredAvailable.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {filteredAvailable.map((badge, idx) => {
                    const IconComponent = BADGE_ICONS[badge.icon] || Award;
                    return (
                      <motion.div
                        key={badge.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.03 }}
                        className="p-3 sm:p-4 text-center bg-[#111] border border-[#222] opacity-60 hover:opacity-80 transition-opacity overflow-hidden"
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 flex items-center justify-center bg-[#1a1a1a] flex-shrink-0">
                          <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                        </div>
                        <h4 className="font-bold text-xs sm:text-sm mb-1 text-gray-400 line-clamp-2">{badge.name}</h4>
                        <p className="text-xs text-gray-600 mb-2 line-clamp-2 hidden sm:block">{badge.description}</p>
                        <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                          <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 bg-[#222] text-gray-500 uppercase">
                            {badge.rarity}
                          </span>
                          <span className="text-[10px] sm:text-xs text-gray-600">+{badge.points} pts</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </div>

          {/* Leaderboard Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
            >
              <h3 className="font-heading text-base sm:text-lg font-bold mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-400" />
                Leaderboard
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {leaderboard.slice(0, 10).map((entry, idx) => (
                  <div 
                    key={entry.user_id}
                    className={`flex items-center gap-2 sm:gap-3 p-2 ${
                      entry.user_id === user?.user_id ? "bg-crimson/10 border border-crimson/30" : ""
                    }`}
                  >
                    <span className={`w-6 text-center font-bold ${
                      idx === 0 ? "text-yellow-400" :
                      idx === 1 ? "text-gray-300" :
                      idx === 2 ? "text-orange-400" :
                      "text-gray-500"
                    }`}>
                      {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${entry.rank}`}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{entry.name}</p>
                      <p className="text-gray-500 text-xs">{entry.badge_count} badges</p>
                    </div>
                    <span className="text-crimson font-bold text-sm">{entry.total_points}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Points Breakdown */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <h3 className="font-heading text-lg font-bold mb-4">Your Points</h3>
              <div className="text-center py-4">
                <div className="text-4xl font-bold text-crimson mb-2">{badgeData?.total_points || 0}</div>
                <p className="text-gray-500 text-sm">Total Points Earned</p>
              </div>
              <div className="mt-4 pt-4 border-t border-[#333]">
                <p className="text-xs text-gray-500 text-center">
                  Earn more points by completing achievements!
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Share Modal */}
        <AnimatePresence>
          {shareModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
              onClick={() => setShareModal(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-obsidian-light border border-[#333] p-6 max-w-md w-full mx-4"
                data-testid="share-badge-modal"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-xl font-bold">
                    Share <span className="text-crimson">{shareModal.name}</span>
                  </h3>
                  <button 
                    onClick={() => setShareModal(null)}
                    className="p-1 hover:bg-[#222]"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                <div className="text-center mb-6">
                  <div 
                    className="w-16 h-16 mx-auto mb-3 flex items-center justify-center"
                    style={{ 
                      background: shareModal.rarity_colors?.bg,
                      border: `2px solid ${shareModal.rarity_colors?.border}`
                    }}
                  >
                    {(() => {
                      const Icon = BADGE_ICONS[shareModal.icon] || Award;
                      return <Icon className="w-8 h-8" style={{ color: shareModal.rarity_colors?.text }} />;
                    })()}
                  </div>
                  <p className="text-gray-400 text-sm">{shareModal.description}</p>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => shareBadgeOnLinkedIn(shareModal)}
                    className="w-full bg-[#0077b5] hover:bg-[#006097]"
                    data-testid="share-badge-linkedin"
                  >
                    <Linkedin className="w-4 h-4 mr-2" />
                    Share on LinkedIn
                  </Button>
                  <Button
                    onClick={() => shareBadgeOnTwitter(shareModal)}
                    className="w-full bg-[#1da1f2] hover:bg-[#0d8ddb]"
                    data-testid="share-badge-twitter"
                  >
                    <Twitter className="w-4 h-4 mr-2" />
                    Share on Twitter
                  </Button>
                  <Button
                    onClick={() => copyBadgeLink(shareModal)}
                    variant="outline"
                    className="w-full border-[#333] text-white hover:bg-[#222]"
                    data-testid="share-badge-copy"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Copy to Clipboard
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default BadgesPage;
